"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.createAuthProvider = void 0;
const jsx_runtime_1 = require("react/jsx-runtime");
const react_1 = require("react");
const hashToken_1 = require("./hashToken");
function createAuthProvider(options) {
    const { tokenKey, userKey, loginAndGetSession, consumeHashToken } = options;
    const AuthContext = (0, react_1.createContext)(undefined);
    function useAuth() {
        const ctx = (0, react_1.useContext)(AuthContext);
        if (!ctx)
            throw new Error('useAuth must be used within AuthProvider');
        return ctx;
    }
    const AuthProvider = ({ children }) => {
        const [user, setUser] = (0, react_1.useState)(null);
        const [loading, setLoading] = (0, react_1.useState)(true);
        (0, react_1.useEffect)(() => {
            const restoreFromStorage = () => {
                const token = localStorage.getItem(tokenKey);
                const saved = localStorage.getItem(userKey);
                if (token && saved) {
                    try {
                        setUser(JSON.parse(saved));
                    }
                    catch {
                        localStorage.removeItem(tokenKey);
                        localStorage.removeItem(userKey);
                    }
                }
                setLoading(false);
            };
            const init = async () => {
                if (consumeHashToken) {
                    const hashToken = (0, hashToken_1.getTokenFromHash)();
                    if (hashToken) {
                        (0, hashToken_1.clearHash)();
                        // fetchUser calls the API using whatever's in localStorage under tokenKey (via the
                        // app's own getAuthHeaders()) — write it first so that call is actually authenticated.
                        localStorage.setItem(tokenKey, hashToken);
                        const fetchedUser = await consumeHashToken.fetchUser(hashToken).catch(() => null);
                        if (fetchedUser) {
                            localStorage.setItem(userKey, JSON.stringify(fetchedUser));
                            setUser(fetchedUser);
                            setLoading(false);
                            return;
                        }
                        // Handoff token didn't resolve to a user — undo and fall through to whatever's already stored.
                        localStorage.removeItem(tokenKey);
                    }
                }
                restoreFromStorage();
            };
            init();
        }, []);
        const login = async (email, password) => {
            const session = await loginAndGetSession(email, password);
            if (!session)
                return false;
            localStorage.setItem(tokenKey, session.token);
            localStorage.setItem(userKey, JSON.stringify(session.user));
            setUser(session.user);
            return true;
        };
        const logout = () => {
            localStorage.removeItem(tokenKey);
            localStorage.removeItem(userKey);
            setUser(null);
        };
        return ((0, jsx_runtime_1.jsx)(AuthContext.Provider, { value: { user, isAuthenticated: !!user, loading, login, logout }, children: children }));
    };
    return { AuthProvider, useAuth };
}
exports.createAuthProvider = createAuthProvider;
